import { supabase } from './supabase'

/**
 * Enable 2FA for user
 */
export const enable2FA = async (method = 'totp') => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // For TOTP, we'd generate a secret (typically done server-side)
    // For now, we'll create a placeholder
    const secret = method === 'totp' ? 'placeholder-secret' : null

    const { data, error } = await supabase
      .from('user_2fa')
      .upsert({
        user_id: user.id,
        method,
        secret,
        is_enabled: false, // Will be enabled after verification
        updated_at: new Date().toISOString(),
      })
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error enabling 2FA:', error)
    throw error
  }
}

/**
 * Verify and enable 2FA
 */
export const verifyAndEnable2FA = async (code) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // Verify TOTP code (would need server-side verification)
    // For now, we'll just enable it
    const { data, error } = await supabase
      .from('user_2fa')
      .update({
        is_enabled: true,
        last_used_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', user.id)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error verifying 2FA:', error)
    throw error
  }
}

/**
 * Disable 2FA
 */
export const disable2FA = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { error } = await supabase
      .from('user_2fa')
      .update({
        is_enabled: false,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', user.id)

    if (error) throw error
    return { success: true }
  } catch (error) {
    console.error('Error disabling 2FA:', error)
    throw error
  }
}

/**
 * Get 2FA status for user
 */
export const get2FAStatus = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    const { data, error } = await supabase
      .from('user_2fa')
      .select('*')
      .eq('user_id', user.id)
      .single()

    if (error && error.code !== 'PGRST116') throw error
    return data || null
  } catch (error) {
    console.error('Error fetching 2FA status:', error)
    return null
  }
}

/**
 * Generate backup codes
 */
export const generateBackupCodes = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // Generate 10 backup codes
    const codes = Array.from({ length: 10 }, () => {
      return Math.random().toString(36).substring(2, 10).toUpperCase()
    })

    const { data, error } = await supabase
      .from('user_2fa')
      .update({
        backup_codes: codes,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', user.id)
      .select()
      .single()

    if (error) throw error
    return codes
  } catch (error) {
    console.error('Error generating backup codes:', error)
    throw error
  }
}

